import {
  getDocuments,
  getDocumentsPastYears,
  getDocumentTypes,
  IDocument,
  IDocumentType,
} from 'API/documents';
import { DOCUMENTS_REDUCER_CASE } from 'Enum/documents';
import { useAppSelector, useIsDesktop } from 'Hooks';
import { useLoader } from 'Hooks/useLoader';
import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { DocumentsReducer, IAction } from 'reducer/Documents/DocumentsReducer';

export interface IExtendedDocument extends IDocument {
  isSentToUser: boolean;
  infoDescription?: string;
  infoLinkText?: string;
  infoLink?: string;
  documentSubject?: string;
}

export interface IDocuments {
  results: {
    todayDocs: IExtendedDocument[];
    thisWeekDocs: IExtendedDocument[];
    thisMonthDocs: IExtendedDocument[];
    thisYearDocs: IExtendedDocument[];
  };
  previousYearsResults: IExtendedDocument[];
  filteredResults: IExtendedDocument[];
}

export interface IDocumentFilter {
  advancedFilterActive: boolean;
  isDocumentSentByUser: boolean | null;
  searchByNameOrSubject: string;
  subjectsTypes: string[];
  datePeriod: {
    lastWeek: boolean;
    lastMonth: boolean;
    lastYear: boolean;
  };
  orderByDescending: boolean;
}

export interface IDocumentsContext {
  types: IDocumentType[];
  setTypes: React.Dispatch<React.SetStateAction<IDocumentType[]>>;
  previousYears: number[];
  documents: IDocuments;
  dispatchDocuments: React.Dispatch<IAction>;
  filter: IDocumentFilter;
  setFilter: React.Dispatch<React.SetStateAction<IDocumentFilter>>;
  advancedFilterActivationHandler: Function;
}

export const DocumentsContext = createContext<IDocumentsContext>({
  types: [],
  setTypes: () => {},
  previousYears: [],
  documents: {
    results: {
      todayDocs: [],
      thisWeekDocs: [],
      thisMonthDocs: [],
      thisYearDocs: [],
    },
    previousYearsResults: [],
    filteredResults: [],
  },
  dispatchDocuments: () => {},
  filter: {
    advancedFilterActive: false,
    isDocumentSentByUser: null,
    searchByNameOrSubject: '',
    subjectsTypes: [],
    datePeriod: {
      lastWeek: false,
      lastMonth: false,
      lastYear: false,
    },
    orderByDescending: true,
  },
  setFilter: () => {},
  advancedFilterActivationHandler: () => {},
});

export const DocumentsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const [types, setTypes] = useState<IDocumentType[]>([]);
  const [previousYears, setPreviousYears] = useState<number[]>([]);
  const [documents, dispatchDocuments] = useReducer(DocumentsReducer, {
    results: {
      todayDocs: [],
      thisWeekDocs: [],
      thisMonthDocs: [],
      thisYearDocs: [],
    },
    previousYearsResults: [],
    filteredResults: [],
  });
  const [filter, setFilter] = useState<IDocumentFilter>({
    advancedFilterActive: false,
    isDocumentSentByUser: true,
    searchByNameOrSubject: '',
    subjectsTypes: [],
    datePeriod: {
      lastWeek: false,
      lastMonth: false,
      lastYear: false,
    },
    orderByDescending: true,
  });

  const { isLoading, setIsLoading, loader } = useLoader();

  const isDesktop = useIsDesktop();

  const initDocuments = useCallback(async () => {
    setIsLoading(true);
    const [documents, documentTypes] = await Promise.all([
      getDocuments(),
      getDocumentTypes(),
    ]);

    if (documents && documentTypes) {
      dispatchDocuments({
        type: isDesktop
          ? DOCUMENTS_REDUCER_CASE.INIT
          : DOCUMENTS_REDUCER_CASE.INIT_MOBILE,
        payload: {
          value: { docs: documents, types: documentTypes },
        },
      });
      setTypes(documentTypes);
    }
    setIsLoading(false);
  }, [setIsLoading, isDesktop]);

  const initPreviousYears = useCallback(async () => {
    const result = await getDocumentsPastYears();
    if (result) setPreviousYears(result);
  }, []);

  useEffect(() => {
    initDocuments();
    initPreviousYears();
  }, [initDocuments, initPreviousYears, imposterId]);

  const advancedFilterActivationHandler = (updatedFilter: IDocumentFilter) => {
    const { searchByNameOrSubject, subjectsTypes, datePeriod } = updatedFilter;

    const isAdvancedFilterActive =
      searchByNameOrSubject.length > 0 ||
      subjectsTypes.length > 0 ||
      datePeriod.lastWeek ||
      datePeriod.lastMonth ||
      datePeriod.lastYear;

    if (isAdvancedFilterActive)
      setFilter({ ...updatedFilter, advancedFilterActive: true });
    else setFilter({ ...updatedFilter, advancedFilterActive: false });
  };

  return (
    <DocumentsContext.Provider
      value={{
        types,
        setTypes,
        previousYears,
        documents,
        dispatchDocuments,
        filter,
        setFilter,
        advancedFilterActivationHandler,
      }}
    >
      {isLoading ? loader : children}
    </DocumentsContext.Provider>
  );
};
